import { FC, ReactNode } from 'react';
import { Box, Chip, FormControlLabel, Paper, Switch, TextField, Typography } from '@mui/material';
import { TPgClientAccessArea } from 'services/api/paymentGateway/types';
import { ACCESS_MESSAGE_MAX_LENGTH } from './form';

type TAccessStatusChipProps = {
	enabled: boolean;
};

/** Green "Open" / red "Blocked". */
export const AccessStatusChip: FC<TAccessStatusChipProps> = ({ enabled }) => (
	<Chip size="small" label={enabled ? 'Open' : 'Blocked'} color={enabled ? 'success' : 'error'} />
);

type TAccessAreaCardProps = {
	title: string;
	description: string;
	/** Extra line under the description (e.g. what the switch does not control). */
	note?: ReactNode;
	value: TPgClientAccessArea;
	/** The value on screen differs from the saved one. */
	changed: boolean;
	error?: string;
	disabled?: boolean;
	onChange: (value: TPgClientAccessArea) => void;
};

/** One area customers may use: on/off switch and the message shown while it is blocked. */
export const AccessAreaCard: FC<TAccessAreaCardProps> = ({
	title,
	description,
	note,
	value,
	changed,
	error,
	disabled,
	onChange,
}) => (
	<Paper
		variant="outlined"
		sx={{
			p: 2,
			height: '100%',
			display: 'flex',
			flexDirection: 'column',
			gap: 1.5,
			borderColor: value.enabled ? undefined : 'error.main',
		}}
	>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
			<Typography color="primary" sx={{ fontWeight: 'bold', fontSize: '0.95rem', flexGrow: 1 }}>
				{title}
			</Typography>
			{changed && <Chip size="small" label="Unsaved" color="warning" variant="outlined" />}
			<AccessStatusChip enabled={value.enabled} />
		</Box>
		<Typography variant="body2" color="text.secondary">
			{description}
		</Typography>
		{note}
		<FormControlLabel
			control={
				<Switch
					checked={value.enabled}
					onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
					disabled={disabled}
				/>
			}
			label={value.enabled ? 'Enabled for customers' : 'Disabled for customers'}
		/>
		<TextField
			size="small"
			label="Message shown while blocked"
			required
			fullWidth
			multiline
			minRows={3}
			maxRows={12}
			value={value.message}
			onChange={(e) => onChange({ ...value, message: e.target.value })}
			disabled={disabled}
			error={!!error}
			helperText={
				<Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
					<span>{error ?? 'Plain text, line breaks allowed'}</span>
					<span>
						{value.message.length} / {ACCESS_MESSAGE_MAX_LENGTH}
					</span>
				</Box>
			}
		/>
	</Paper>
);
